import React, { FC, useState, KeyboardEvent } from "react";
import { Grid, InputLabel, Chip, TextField, Tooltip } from "@mui/material";
import { FormFieldProps } from "../../../utils/formFields";
import useStyles from "../../../components/common/model/styles";
import { InfoOutlined } from "@mui/icons-material";

const FormChipTextField: FC<FormFieldProps> = (props) => {
  const classes = useStyles();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string>("");

  // ✅ Email validation
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // ✅ Add email when valid
  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) return;
    if (!isValidEmail(trimmedEmail)) {
      setError("Invalid email address");
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setError("Email already added");
      return;
    }

    setEmails((prev) => [...prev, trimmedEmail]);
    setInputValue("");
    setError("");
    props?.onChange?.({
      target: { id: props.id, value: [...emails, trimmedEmail] },
    });
  };

  // ✅ Remove email
  const handleDelete = (email: string) => {
    const filtered = emails.filter((e) => e !== email);
    setEmails(filtered);
    props?.onChange?.({ target: { id: props.id, value: filtered } });
  };

  // ✅ Handle key press (Enter or comma)
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail(inputValue);
    }
  };

  const inputRender = (isInGrid: boolean) => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        border: props.error ? "1px solid #d32f2f" : "1px solid #ccc",
        padding: "6px 8px",
        borderRadius: props.isRounded ? "50px" : "6px",
        background: "#fff",
        minHeight: "30px",
        alignItems: "center",
        marginTop: "0px",
      }}
    >
      {emails.map((email) => (
        <Chip
          key={email}
          label={email}
          onDelete={() => handleDelete(email)}
          color="primary"
          size="medium"
        />
      ))}
      <TextField
        name={props?.id}
        id={props?.id}
        variant="standard"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setError("");
        }}
        onKeyDown={handleKeyDown}
        placeholder={props.placeholder || "Enter email and press Enter"}
        InputProps={{ disableUnderline: true }}
        sx={{ flex: 1, minWidth: "30px" }}
      />
    </div>
  );

  return (
    <>
      {props.label && !props?.inSingleGridForm ? (
        <>
          <Grid
            item
            xs={12}
            sm={props?.md ? (props?.firstColmd ? props?.firstColmd : 1.5) : 5}
            className={classes.labelBox}
            sx={{ paddingRight: "0px !important" }}
          >
            <InputLabel
              sx={{
                width: "100%",
                color: "#333333",
                fontWeight: 600,
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
              }}
              shrink
              htmlFor={props.id}
            >
              {props?.info && (
                <span
                  style={{
                    marginLeft: "-2px",
                    marginRight: "2px",
                    paddingTop: "6px",
                    fontSize: "10px",
                  }}
                >
                  <Tooltip title={props?.info}>
                    <InfoOutlined />
                  </Tooltip>
                </span>
              )}
              <span>
                <Tooltip title={props.tooltipText || ""} arrow>
                  <span>{props.label}</span>
                </Tooltip>
              </span>
              {props.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}>*</span>
              )}
            </InputLabel>
          </Grid>

          <Grid
            item
            xs={12}
            sm={props?.md ? props?.md : 12}
            style={{ padding: "0px" }}
          >
            {inputRender(true)}
            {error && (
              <span style={{ color: "red", fontSize: "13px" }}>{error}</span>
            )}
            <p style={{ color: "#02906f", fontSize: "12px" }}>
              Note:- Please press enter key to add tag
            </p>
          </Grid>
        </>
      ) : props?.label && props?.inSingleGridForm ? (
        <Grid
          item
          xs={12}
          sm={props?.md ? props?.md : 12}
          sx={{
            display: "block",
            alignItems: "center",
            gap: 2,
            ...props?.GridFieldStyle,
          }}
        >
          <Tooltip title={props?.tooltipText || ""} arrow>
            <InputLabel
              sx={{
                color: "#637381",
                fontWeight: 600,
                fontSize: "16px",
                textAlign: "left !important",
              }}
              shrink
              htmlFor={props?.id}
            >
              {props?.label}
              {props?.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}>*</span>
              )}
            </InputLabel>
          </Tooltip>
          {inputRender(true)}
        </Grid>
      ) : (
        inputRender(false)
      )}
    </>
  );
};

export default FormChipTextField;
